import { registerPushSubscription } from './registerPush';

let initialized = false;

/**
 * Register the service worker for push notifications (web only)
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported');
    return null;
  }

  try {
    // Register the push service worker
    const registration = await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/',
    });
    console.info('[Push] Service worker registered:', registration.scope);
    
    // Listen for service worker updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'activated') {
            console.info('[Push] New service worker activated, re-registering push subscription...');
            // Re-register push subscription when SW updates
            // Small delay to ensure the new SW is fully ready
            setTimeout(() => {
              if (Notification.permission === 'granted') {
                registerPushSubscription(undefined, false).catch(console.error);
              }
            }, 1000);
          }
        });
      }
    });
    
    return registration;
  } catch (error) {
    console.error('[Push] Service worker registration failed:', error);
    return null;
  }
}

/**
 * Request notification permission and register for push
 * Call this after user logs in
 */
export async function requestNotificationPermission(): Promise<boolean> {
  // For native Capacitor app, we use web push via the WebView
  // The service worker still works in Capacitor's WebView
  
  // For web/PWA
  if (!('Notification' in window)) {
    console.warn('[Push] Notifications not supported in this browser');
    return false;
  }

  // Check current permission
  if (Notification.permission === 'granted') {
    // Already granted, just register subscription
    return registerPushSubscription();
  }

  if (Notification.permission === 'denied') {
    console.warn('[Push] Notifications were denied by user');
    return false;
  }

  // Request permission
  const permission = await Notification.requestPermission();
  if (permission === 'granted') {
    return registerPushSubscription();
  }

  return false;
}

/**
 * Initialize push notification system
 * Call this on app startup
 */
export async function initNotifications(): Promise<void> {
  if (initialized) return;
  initialized = true;

  console.info('[Push] Initializing notifications...');

  // Register service worker (works in both web and Capacitor WebView)
  await registerServiceWorker();
  
  // If permission is already granted, ensure subscription is registered
  // This handles the case where the app was updated and subscription might be stale
  if ('Notification' in window && Notification.permission === 'granted') {
    // Small delay to ensure SW is ready
    setTimeout(async () => {
      try {
        await registerPushSubscription();
        console.info('[Push] Push subscription verified/registered on init');
      } catch (err) {
        console.warn('[Push] Failed to verify subscription on init:', err);
      }
    }, 2000);
  }
}

/**
 * Check if notifications are available and what the permission status is
 */
export function getNotificationStatus(): {
  supported: boolean;
  permission: 'granted' | 'denied' | 'default' | 'unknown';
} {
  if (!('Notification' in window)) {
    return { supported: false, permission: 'unknown' };
  }

  return {
    supported: true,
    permission: Notification.permission as 'granted' | 'denied' | 'default',
  };
}

